import { t } from 'i18next';
import { formatDate } from '../../../utils';
import {
  FilterValue,
  NO_FILTERS,
  TableFilters,
  toQueryParams as toTableQueryParams,
  withSearch
} from '../../Tools/Table/filters';
import { FilterOptions, InterventionStatus, NamedRef, UnsuccessfulStatus } from './types';

export const FOLLOW_UP_API = '/api/bconnect/v2/follow-up/maintenance-orders/';
export const FOLLOW_UP_ATTACHMENTS_API = '/api/bconnect/v2/follow-up/attachments/';
export const FOLLOW_UP_ROUTE = '/bconnect/follow-up';

/** Onglets de statut, dans l'ordre de la maquette ; `null` = « Toutes ». */
export const STATUS_TABS: (InterventionStatus | null)[] = [
  null,
  'NEW',
  'DONE',
  'IMPOSSIBLE',
  'EXPIRED',
  'CANCELED_BY_BTECH_USER'
];

/** Tous les statuts, dans l'ordre de la légende de la carte. */
export const ALL_STATUSES: InterventionStatus[] = [
  'NEW',
  'IN_PROGRESS',
  'DONE',
  'IMPOSSIBLE',
  'EXPIRED',
  'CANCELED_BY_BTECH_USER'
];

export const UNSUCCESSFUL_STATUSES: UnsuccessfulStatus[] = [
  'TO_BE_TREATED',
  'CLIENT_INFO_STANDBY',
  'TO_RESCHEDULE',
  'RESCHEDULED',
  'ABANDONED'
];

export type FollowUpAction = 'cancel' | 'reassign' | 'unsuccessful';

/**
 * Ce que chaque action exige : la section qui la donne, et le statut des interventions qu'elle
 * accepte (annuler et réaffecter une planifiée, suivre une impossible). Le serveur applique la
 * même règle ; l'écran s'en sert pour ne proposer une action qu'à bon escient.
 */
export const ACTION_RULES: Record<FollowUpAction, { section: string; status: InterventionStatus }> =
  {
    reassign: { section: 'BCONNECT_FOLLOW_UP_REASSIGN', status: 'NEW' },
    unsuccessful: { section: 'BCONNECT_FOLLOW_UP_UNSUCCESSFUL', status: 'IMPOSSIBLE' },
    cancel: { section: 'BCONNECT_FOLLOW_UP_CANCEL', status: 'NEW' }
  };

/** Ordre d'affichage des actions, la destructive en dernier. */
export const ACTIONS: FollowUpAction[] = ['reassign', 'unsuccessful', 'cancel'];

/** L'action est permise à l'utilisateur, quel que soit le statut. */
export function isActionAllowed(
  action: FollowUpAction,
  canAccess: (section: string) => boolean
): boolean {
  return canAccess(ACTION_RULES[action].section);
}

/**
 * L'action s'applique : au moins une intervention, toutes dans le statut qu'elle accepte. Un
 * statut inconnu (ligne cochée qui n'est plus affichée) ne permet pas de l'affirmer.
 */
export function isActionApplicable(
  action: FollowUpAction,
  statuses: (InterventionStatus | undefined)[]
): boolean {
  return statuses.length > 0 && statuses.every((status) => status === ACTION_RULES[action].status);
}

export type ChipColor = 'default' | 'primary' | 'info' | 'warning' | 'success' | 'error';

/** Couleurs des pastilles de statut, reprises des règles d'écran d'Emma. */
export const STATUS_COLOR: Record<InterventionStatus, ChipColor> = {
  NEW: 'info',
  IN_PROGRESS: 'warning',
  DONE: 'success',
  IMPOSSIBLE: 'error',
  EXPIRED: 'default',
  CANCELED_BY_BTECH_USER: 'default'
};

/** Couleurs des points de la carte, mêmes teintes que les pastilles. */
export const STATUS_DOT: Record<InterventionStatus, string> = {
  NEW: '#95CDE8',
  IN_PROGRESS: '#F7A677',
  DONE: '#7ED17C',
  IMPOSSIBLE: '#D32F2F',
  EXPIRED: '#9E9E9E',
  CANCELED_BY_BTECH_USER: '#616161'
};

/**
 * L'état de la liste : la recherche universelle, l'onglet de statut, et les filtres du
 * bloc dépliable, nommés comme les paramètres du `FollowUpFilter` du back.
 */
export type FollowUpQuery = {
  search: string;
  status: InterventionStatus | null;
  filters: TableFilters;
};

export const EMPTY_QUERY: FollowUpQuery = { search: '', status: null, filters: NO_FILTERS };

/** Les filtres de date, hors des listes déroulantes partagées. */
export const DATE_FILTERS = [
  'created_at_after',
  'created_at_before',
  'made_date_after',
  'made_date_before'
] as const;

/** Sous ce seuil, le back ne fait pas de recherche « contient » (index trigrammes). */
export const SEARCH_MIN_LENGTH = 3;

/** Une saisie trop courte pour être cherchée, numéro d'intervention compris. */
export function isSearchTooShort(raw: string): boolean {
  const value = raw.trim();
  return value.length > 0 && value.length < SEARCH_MIN_LENGTH;
}

export function toQueryParams(query: FollowUpQuery): Record<string, string> {
  // Une saisie trop courte n'est pas envoyée : la liste reste telle quelle au lieu de
  // tomber à vide, et l'écran invite à compléter.
  const search = isSearchTooShort(query.search) ? '' : query.search;
  return toTableQueryParams(withSearch({ ...query.filters, status__in: query.status }, search));
}

/** Nombre de filtres actifs du bloc dépliable, pour le libellé « Filtres (N) ». */
export function activeFilterCount(query: FollowUpQuery): number {
  return Object.keys(toTableQueryParams(query.filters)).length;
}

export type ColumnKey =
  | 'reference'
  | 'type'
  | 'city'
  | 'pds'
  | 'agent'
  | 'status'
  | 'updatedAt'
  | 'client'
  | 'module'
  | 'sensor'
  | 'origin';

export const ALL_COLUMNS: ColumnKey[] = [
  'reference',
  'type',
  'city',
  'pds',
  'agent',
  'status',
  'updatedAt',
  'client',
  'module',
  'sensor',
  'origin'
];

export const DEFAULT_COLUMNS: ColumnKey[] = [
  'reference',
  'type',
  'city',
  'pds',
  'agent',
  'status',
  'updatedAt'
];

/**
 * La clé du choix de colonnes, propre à l'utilisateur : deux personnes sur le même navigateur ne
 * partagent pas leur choix. Il reste attaché au navigateur, pas encore aux préférences du compte.
 */
export function columnsStorageKey(userId: number | null | undefined): string {
  return `bconnect-follow-up-columns:${userId ?? 'anonymous'}`;
}

/** Le choix de colonnes mémorisé ; les défauts si rien de valide n'est stocké. */
export function loadColumns(storage: Pick<Storage, 'getItem'> | null, key: string): ColumnKey[] {
  try {
    const stored: unknown = JSON.parse(storage?.getItem(key) ?? 'null');
    if (Array.isArray(stored)) {
      const kept = ALL_COLUMNS.filter((column) => stored.includes(column));
      if (kept.length) {
        return kept;
      }
    }
  } catch {
    // Valeur corrompue : on retombe sur les colonnes par défaut.
  }
  return DEFAULT_COLUMNS;
}

export function saveColumns(
  storage: Pick<Storage, 'setItem'> | null,
  key: string,
  columns: ColumnKey[]
): void {
  try {
    storage?.setItem(key, JSON.stringify(columns));
  } catch {
    // Stockage indisponible (navigation privée) : le choix vaut pour la session seulement.
  }
}

/** Lecture sûre d'une valeur du `Point` legacy, à n'importe quelle profondeur. */
export function readPoint(point: Record<string, unknown> | null, ...path: string[]): unknown {
  let current: unknown = point;
  for (const key of path) {
    if (current === null || typeof current !== 'object' || Array.isArray(current)) {
      return undefined;
    }
    // Le garde ci-dessus a établi un objet non nul : seul l'index par chaîne reste à typer.
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

export function displayValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'boolean') {
    return t(value ? 'followUp.detail.yes' : 'followUp.detail.no');
  }
  if (typeof value === 'object') {
    // Un objet `{Id, Label}` du Point legacy : les clés lues sont toutes facultatives et
    // retombent sur `undefined`, donc la lecture reste sûre quelle que soit sa forme.
    const labelled = value as { Label?: unknown; label?: unknown; Value?: unknown };
    const label = labelled.Label ?? labelled.label ?? labelled.Value;
    return label === undefined ? null : displayValue(label);
  }
  return String(value);
}

/**
 * Une date de l'API au format commun de B-Tech (`formatDate`), ou `null` quand elle manque :
 * l'API renvoie `null` ou rien pour une date non renseignée.
 */
export function formatOptionalDate(value: unknown, language: string): string | null {
  return typeof value === 'string' && value ? formatDate(value, language) : null;
}

/** Les consignes d'une replanification, lues sans présumer de la forme de l'événement. */
export function rescheduleInstructions(payload: Record<string, unknown>): string | null {
  const reschedule = payload.reschedule;
  if (typeof reschedule !== 'object' || reschedule === null || !('instructions' in reschedule)) {
    return null;
  }
  return typeof reschedule.instructions === 'string' && reschedule.instructions
    ? reschedule.instructions
    : null;
}

/**
 * Les clients du périmètre, déduits des contrats que l'API propose au filtre (ceux de
 * l'utilisateur) : un client par contrat distinct, triés par nom.
 */
export function customersOf(contracts: FilterOptions['contracts']): NamedRef[] {
  const byId = new Map(contracts.map((contract) => [contract.customer_id, contract.customer]));
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

type FilterContract = FilterOptions['contracts'][number];

/** Les valeurs d'un filtre multiple, en chaînes : la liste les porte en nombres ou en chaînes. */
function selectedValues(value: FilterValue | undefined): string[] {
  if (Array.isArray(value)) {
    return value.map(String);
  }
  return value ? [value] : [];
}

/**
 * Les contrats proposés au filtre : ceux des clients choisis, ou tous sans client choisi. Un
 * couple client / contrat incompatible donnerait une liste vide sans explication.
 */
export function contractsOfCustomers(
  contracts: FilterContract[],
  customerFilter: FilterValue | undefined
): FilterContract[] {
  const customers = selectedValues(customerFilter);
  return customers.length
    ? contracts.filter((contract) => customers.includes(String(contract.customer_id)))
    : contracts;
}

/**
 * Pose le filtre client et retire les contrats qui n'appartiennent plus aux clients choisis,
 * pour que les deux filtres restent compatibles.
 */
export function withCustomers(
  filters: TableFilters,
  customerFilter: FilterValue,
  contracts: FilterContract[]
): TableFilters {
  const allowed = new Set(
    contractsOfCustomers(contracts, customerFilter).map((contract) => String(contract.id))
  );
  const chosen = filters.contract__in;
  return {
    ...filters,
    customer__in: customerFilter,
    contract__in: Array.isArray(chosen) ? chosen.filter((id) => allowed.has(String(id))) : chosen
  };
}
